import {
    AccountHeader,
    AccountLayout,
    isTokenExpired,
    logOff,
    NiasHeader,
    sessionCache,
    signInSuccess,
    Timer,
    tokenRenew,
    useAdIdleLogout,
} from "modules/auth";
import { Footer, MainLayout, RoleType } from "modules/common";
import { getProfile } from "modules/dashboard";
import { Educations } from "modules/education";
import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Route, Switch, useLocation } from "react-router-dom";
import { PrivateRoute } from "routing";
import {
    ActivateAccount,
    AgroclimateStations,
    Agrometeorology,
    ChangePassword,
    Communication,
    ForgotPassword,
    InteractiveMap,
    ModulesAdministration,
    ModuleVersionsAdministration,
    MySensorics,
    News,
    NoMatch,
    // Dashboard,
    NotAuthorized,
    PhytoViewAdministration,
    PlantProtection,
    Presentations,
    Profile,
    Records,
    SignIn,
    SignUp,
    SystemAdministration,
    TermsOfUse,
    PrivacyPolicy,
    UserActivity,
    UserAdministration,
    UserNotifications,
} from "views";
import { ActiveDomainUsers } from "views/ActiveDomainUsers";
import { Beekeeping } from "views/Beekeeping";
import { CodebookAdministration } from "views/CodebookAdministration";
import EducationAnalysis from "views/EducationAnalysis";
import { EducationDocuments } from "views/EducationDocuments";
import { ExternalModules } from "views/ExternalModules";
import { Home } from "views/Home";
import { MobileAppsPortal } from "views/MobileAppsPortal";
import { SemisAdministration } from "views/SemisAdministration";
import { SignInAd } from "views/SignInAd";
import { AppRoutes } from "./AppRoutes";
import { Unauthorized } from "views/Unauthorized";

const cache = sessionCache();
const timer = Timer.getInstance();

const getSessionData = () => {
    const session = cache.getSession();
    return {
        session,
        expiration: session?.expiration ?? 0,
        refreshTokenExpiration: session?.refreshExpiration ?? 0,
    };
};

export const Routing: React.FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const sessionInitialized = useRef(false);
    useAdIdleLogout();

    useEffect(() => {
        const { session, refreshTokenExpiration } = getSessionData();
        
        if (session && !isTokenExpired(refreshTokenExpiration)) {
            if (!sessionInitialized.current) {
                dispatch(
                    signInSuccess({
                        ...session,
                        role: {
                            value: session.roleType?.value,
                            label: session.roleType?.label,
                        },
                        showChangeEntityDialog: false,
                    })
                );
                dispatch(getProfile());
                sessionInitialized.current = true;
            }
        } else {
            const params = new URLSearchParams(window.location.search);
            const hasTokenParam = params.has("token");
            
            if (!hasTokenParam) {
                sessionInitialized.current = false;
                dispatch(logOff());
            }
        }
    }, [dispatch, location.pathname]);

    useEffect(() => {
        const { expiration } = getSessionData();
        
        if (!expiration) return;
        
        timer.addHandler(() => {
            dispatch(tokenRenew());
        });
        timer.start(expiration);

        return () => timer.cancel();
    });

    return (
        <>
            <NiasHeader />
            <Switch>
                <Route path={AppRoutes.Home} exact>
                <MainLayout>
                    <Home />
                </MainLayout>
            </Route>
            <Route path={AppRoutes.SignIn} exact>
                <AccountLayout header={<AccountHeader />} footer={<Footer />}>
                    <SignIn />
                </AccountLayout>
            </Route>
            <Route path={AppRoutes.SignInAd} exact>
                <SignInAd />
            </Route>
            <Route path={AppRoutes.ForgotPassword} exact>
                <AccountLayout header={<AccountHeader />} footer={<Footer />}>
                    <ForgotPassword />
                </AccountLayout>
            </Route>
            <Route path={AppRoutes.ChangePassword} exact>
                <AccountLayout header={<AccountHeader />} footer={<Footer />}>
                    <ChangePassword />
                </AccountLayout>
            </Route>
            <Route path={AppRoutes.SignUp} exact>
                <AccountLayout header={<AccountHeader />} footer={<Footer />}>
                    <SignUp />
                </AccountLayout>
            </Route>
            <Route path={AppRoutes.TermsOfUse} exact>
                <TermsOfUse />
            </Route>
            <Route path={AppRoutes.PrivacyPolicy} exact>
                <PrivacyPolicy />
            </Route>
            <Route path={AppRoutes.Unauthorized} exact>
                <AccountLayout header={<AccountHeader />} footer={<Footer />}>
                    <Unauthorized />
                </AccountLayout>
            </Route>
            <Route exact path={AppRoutes.ActivateAccount}>
                <ActivateAccount />
            </Route>
            <Route exact path={AppRoutes.NotAuthorized}>
                <NotAuthorized />
            </Route>
            {/* <PrivateRoute path={AppRoutes.Root} exact>
        <MainLayout>
          <Dashboard />
        </MainLayout>
      </PrivateRoute> */}
            <Route path={AppRoutes.News}>
                <MainLayout>
                    <News />
                </MainLayout>
            </Route>
            <Route path={AppRoutes.Agrometeorology}>
                <MainLayout>
                    <Agrometeorology />
                </MainLayout>
            </Route>
            <PrivateRoute
                roleRequirements={[
                    RoleType.Farmer,
                    RoleType.MinistryAdviser,
                    RoleType.SuperAdmin,
                    RoleType.EquipmentAdmin,
                    RoleType.CommunicatorAdviser,
                    RoleType.PrivateAdviser,
                    RoleType.CommunicatorOfficial,
                    RoleType.MinistryOfficial,
                ]}
                path={AppRoutes.AgroclimateStations}
                exact
            >
                <MainLayout>
                    <AgroclimateStations />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.Farmer, RoleType.MinistryAdviser, RoleType.SuperAdmin]}
                path={AppRoutes.InteractiveMap}
                exact
            >
                <MainLayout>
                    <InteractiveMap />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.SuperAdmin,
                    RoleType.EquipmentAdmin,
                    RoleType.CommunicatorAdviser,
                    RoleType.MinistryAdviser,
                    RoleType.CommunicatorOfficial,
                    RoleType.MinistryOfficial,
                    RoleType.PrivateAdviser,
                    RoleType.Farmer,
                ]}
                path={AppRoutes.MySensorics}
                exact
            >
                <MainLayout>
                    <MySensorics />
                </MainLayout>
            </PrivateRoute>
            <Route path={AppRoutes.PlantProtection}>
                <MainLayout>
                    <PlantProtection />
                </MainLayout>
            </Route>
            <PrivateRoute path={AppRoutes.Records} exact>
                <MainLayout>
                    <Records />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.SuperAdmin,
                    RoleType.CommunicatorOfficial,
                    RoleType.CommunicatorAdviser,
                ]}
                path={AppRoutes.Communication}
            >
                <MainLayout>
                    <Communication />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute path={AppRoutes.UserNotifications} exact>
                <MainLayout>
                    <UserNotifications />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.MinistryAdviser, RoleType.SuperAdmin]}
                path={AppRoutes.Presentations}
                exact
            >
                <MainLayout>
                    <Presentations />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.MinistryAdviser,
                    RoleType.MinistryOfficial,
                    RoleType.SuperAdmin,
                ]}
                path={AppRoutes.UserAdministration}
                exact
            >
                <MainLayout>
                    <UserAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.MinistryAdviser,
                    RoleType.MinistryOfficial,
                    RoleType.SuperAdmin,
                ]}
                path={AppRoutes.ActiveDirectoryUsers}
                exact
            >
                <MainLayout>
                    <ActiveDomainUsers />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute roleRequirements={[RoleType.Farmer]} path={AppRoutes.Education} exact>
                <MainLayout>
                    <Educations />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.UserActivity}
                exact
            >
                <MainLayout>
                    <UserActivity />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute path={AppRoutes.Profile} exact>
                <MainLayout>
                    <Profile />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.SystemAdministration}
                exact
            >
                <MainLayout>
                    <SystemAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute roleRequirements={[RoleType.SuperAdmin]} path={AppRoutes.Communication}>
                <MainLayout>
                    <Communication />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.ModulesAdministration}
                exact
            >
                <MainLayout>
                    <ModulesAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.ModuleVersionsAdministration}
                exact
            >
                <MainLayout>
                    <ModuleVersionsAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.SemisAdministrationModule}
                exact
            >
                <MainLayout>
                    <SemisAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.EquipmentAdmin,
                    RoleType.SuperAdmin,
                    RoleType.CommunicatorAdviser,
                    RoleType.MinistryAdviser,
                    RoleType.PrivateAdviser,
                ]}
                path={AppRoutes.EducationAnalysis}
                exact
            >
                <MainLayout>
                    <EducationAnalysis />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.PhytoViewAdministration}
                exact
            >
                <MainLayout>
                    <PhytoViewAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.Beekeeping}
                exact
            >
                <MainLayout>
                    <Beekeeping />
                </MainLayout>
            </PrivateRoute>
            <Route path={AppRoutes.ExternalModules} exact>
                <MainLayout>
                    <ExternalModules />
                </MainLayout>
            </Route>
            <PrivateRoute roleRequirements={[RoleType.SuperAdmin]} path={AppRoutes.Codebook} exact>
                <MainLayout>
                    <CodebookAdministration />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[
                    RoleType.SuperAdmin,
                    RoleType.EquipmentAdmin,
                    RoleType.CommunicatorAdviser,
                    RoleType.MinistryAdviser,
                ]}
                path={AppRoutes.EducationDocuments}
                exact
            >
                <MainLayout>
                    <EducationDocuments />
                </MainLayout>
            </PrivateRoute>
            <PrivateRoute
                roleRequirements={[RoleType.SuperAdmin]}
                path={AppRoutes.MobileAppPortal}
                exact
            >
                <MainLayout>
                    <MobileAppsPortal />
                </MainLayout>
            </PrivateRoute>
                <Route>
                    <NoMatch />
                </Route>
            </Switch>
        </>
    );
};
